import Session from "@lib/session";
import {
  FareKeyLookupDocument,
  FareKeyLookupQuery,
  FareKeyLookupQueryVariables,
} from "@graphql/api";

export default async function fareKeyLookup(
  location: be.Location,
  serviceArea?: number | number[],
): Promise<be.FareKeyLookupReturn> {
  serviceArea ||= Session.getConfig().serviceArea;

  const { data }: { data: FareKeyLookupQuery } = await global.window.apolloClient.query({
    query: FareKeyLookupDocument,
    variables: queryVariables(location, serviceArea),
  });
  const result = { ...data.fareKeyLookup };

  // If address is blank, try to grab it from the google object.
  if (result.location && location.googleObj) {
    result.location = { ...result.location };
    if (!result.location.address) {
      result.location.displayAddress = grabAddressFromGoogleObj(location.googleObj);
      result.location.address = grabAddressFromGoogleObj(location.googleObj);
    }
    if (!result.location.city) {
      result.location.city = location.googleObj.address_components?.find((c) =>
        c.types.includes("locality"),
      )?.short_name;
    }
    if (!result.location.state) {
      result.location.state = location.googleObj.address_components?.find((c) =>
        c.types.includes("administrative_area_level_1"),
      )?.short_name;
    }
    if (!result.location.zip) {
      result.location.zip = location.googleObj.address_components?.find((c) =>
        c.types.includes("postal_code"),
      )?.short_name;
    }
    if (!result.location.latLng)
      result.location.latLng = {
        lat: location.googleObj.geometry?.location?.lat(),
        lng: location.googleObj.geometry?.location?.lng(),
      };
  }

  // Need to add google object back in because it is used to determine if this location is
  // too general on the validation step.
  return result.error
    ? {
        error: true,
        errorMessage: result.errorMessage,
        fklRequest: result.fklRequest,
        fklResponse: result.fklResponse,
      }
    : {
        fklRequest: result.fklRequest,
        fklResponse: result.fklResponse,
        location: { googleObj: location.googleObj, ...(result.location || {}) },
        lookupType: result.lookupType,
        nearbyLocations: result.nearbyLocations?.map((loc: be.Location) => ({ ...loc })) || [],
      };
}

function cleanGoogleObj(googleObj: be.RealisticPlacesResult) {
  if (!googleObj) return {};

  // Strip googleObj
  delete googleObj?.current_opening_hours;
  delete googleObj?.international_phone_number;
  delete googleObj?.price_level;
  delete googleObj?.rating;
  delete googleObj?.opening_hours;
  delete googleObj?.secondary_opening_hours;
  delete googleObj?.reviews;
  delete googleObj?.formatted_phone_number;
  delete googleObj?.url;
  delete googleObj?.user_ratings_total;
  delete googleObj?.html_attributions;
  delete googleObj?.icon;
  delete googleObj?.photos;
  delete googleObj?.website;
  delete googleObj?.utc_offset;
  delete googleObj?.geometry?.viewport;
  delete googleObj?.icon_background_color;
  delete googleObj?.icon_mask_base_uri;
  delete googleObj?.reference;
  delete googleObj?.adr_address;
  delete googleObj?.secondChanceFkl; // Our own property to indicate that this location has already been through the FKL process twice.

  const clonedGoogleObj = JSON.parse(JSON.stringify(googleObj));
  return clonedGoogleObj;
}

function grabAddressFromGoogleObj(googleObj: be.RealisticPlacesResult) {
  let route = googleObj.address_components?.find((c) => c.types.includes("route"))?.short_name;
  let streetNumber = googleObj.address_components?.find((c) => c.types.includes("street_number"))
    ?.short_name;
  return streetNumber ? `${streetNumber} ${route}` : route;
}

function queryVariables(
  location: be.Location,
  serviceArea?: number | number[],
): FareKeyLookupQueryVariables {
  const queryVars: FareKeyLookupQueryVariables = {
    displayAddress: location.displayAddress,
    displayName: location.displayName,
    groupProfile: Session.getBooking().profiles.group,
    typeString: location.typeString,
    fareCode: location.code,
    serviceAreas: serviceArea,
    sourceType: Number.isInteger(Session.getBooking().activeLegs()[0]?.sourceType)
      ? Session.getBooking().activeLegs()[0].sourceType
      : 60,
  };
  if (location.googleObj)
    queryVars.googleObject = JSON.stringify(cleanGoogleObj(location.googleObj));

  return queryVars;
}
